import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, Plus, Trash2, Receipt, CheckCircle2 } from "lucide-react";
import { API_BASE } from "../../lib/site";

const fmtBRL = (v) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const inputCls =
    "w-full rounded-xl border-2 border-[#1A231E]/12 bg-white px-4 py-2.5 text-sm font-semibold text-[#1A231E] focus:outline-none focus:border-[#F57A3A] transition-colors";

export default function AdminVendas() {
    const [vendas, setVendas] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [rows, setRows] = useState([{ produto_id: "", qtd: 1 }]);
    const [cupom, setCupom] = useState("");
    const [msg, setMsg] = useState(null);

    const load = () => {
        axios.get(`${API_BASE}/vendas`, { withCredentials: true }).then(({ data }) => setVendas(data)).catch(() => {});
        axios.get(`${API_BASE}/produtos/todos`, { withCredentials: true }).then(({ data }) => setProdutos(data.filter((p) => p.ativo))).catch(() => {});
    };

    useEffect(() => {
        load();
    }, []);

    const hoje = new Date().toISOString().slice(0, 10);
    const seteDias = new Date(Date.now() - 7 * 86400000);
    const totalHoje = vendas.filter((v) => v.created_at.startsWith(hoje)).reduce((s, v) => s + v.total, 0);
    const totalSemana = vendas.filter((v) => new Date(v.created_at) >= seteDias).reduce((s, v) => s + v.total, 0);
    const totalGeral = vendas.reduce((s, v) => s + v.total, 0);
    const ticket = vendas.length ? totalGeral / vendas.length : 0;

    const setRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    const submit = async (e) => {
        e.preventDefault();
        setMsg(null);
        const items = rows.filter((r) => r.produto_id && r.qtd > 0);
        if (!items.length) {
            setMsg({ ok: false, text: "Selecione pelo menos um produto." });
            return;
        }
        try {
            await axios.post(`${API_BASE}/vendas`, { items, cupom: cupom || null }, { withCredentials: true });
            setRows([{ produto_id: "", qtd: 1 }]);
            setCupom("");
            setMsg({ ok: true, text: "Venda registrada e estoque atualizado!" });
            load();
        } catch (err) {
            setMsg({ ok: false, text: err.response?.data?.detail || "Erro ao registrar venda" });
        }
    };

    return (
        <section id="admin-vendas" data-testid="admin-vendas-section" className="scroll-mt-28">
            <div className="flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-[#0E6B3F]" />
                <h2 className="text-2xl font-extrabold">Levantamento de vendas</h2>
            </div>

            <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3.5" data-testid="vendas-totais">
                {[
                    { label: "Hoje", valor: fmtBRL(totalHoje) },
                    { label: "Últimos 7 dias", valor: fmtBRL(totalSemana) },
                    { label: "Total geral", valor: fmtBRL(totalGeral) },
                    { label: "Ticket médio", valor: fmtBRL(ticket) },
                ].map((c) => (
                    <div key={c.label} className="rounded-2xl bg-[#0E6B3F] text-[#F7F4EC] p-4.5 p-4">
                        <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#F1C75B]">{c.label}</p>
                        <p className="mt-1 text-xl font-black">{c.valor}</p>
                    </div>
                ))}
            </div>

            <form onSubmit={submit} data-testid="admin-venda-form" className="mt-5 rounded-[1.75rem] bg-white border-2 border-[#1A231E]/12 sticker-soft p-6">
                <h3 className="font-extrabold">Registrar venda (totem)</h3>
                <div className="mt-4 space-y-3">
                    {rows.map((r, i) => (
                        <div key={i} className="flex gap-2.5 items-center">
                            <select
                                data-testid={`venda-produto-${i}`}
                                value={r.produto_id}
                                onChange={(e) => setRow(i, { produto_id: e.target.value })}
                                className={`${inputCls} flex-1`}
                            >
                                <option value="">Produto...</option>
                                {produtos.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.nome} — {fmtBRL(p.preco)} (estoque {p.estoque})
                                    </option>
                                ))}
                            </select>
                            <input
                                type="number"
                                min="1"
                                data-testid={`venda-qtd-${i}`}
                                value={r.qtd}
                                onChange={(e) => setRow(i, { qtd: parseInt(e.target.value) || 1 })}
                                className={`${inputCls} w-20 text-center`}
                            />
                            <button
                                type="button"
                                onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
                                aria-label="Remover item"
                                className="w-9 h-9 rounded-lg grid place-items-center text-red-500 hover:bg-red-50 transition-colors shrink-0"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2.5 items-center">
                    <button type="button" data-testid="venda-add-item" onClick={() => setRows([...rows, { produto_id: "", qtd: 1 }])} className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#0E6B3F] text-[#0E6B3F] text-sm font-extrabold px-4 py-2 hover:bg-[#0E6B3F]/5 transition-colors">
                        <Plus className="w-4 h-4" />
                        Item
                    </button>
                    <input
                        data-testid="venda-cupom"
                        value={cupom}
                        onChange={(e) => setCupom(e.target.value.toUpperCase())}
                        placeholder="Cupom (opcional)"
                        className={`${inputCls} w-48 uppercase`}
                    />
                    <button type="submit" data-testid="venda-submit" className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#F57A3A] hover:bg-[#E06324] text-[#F7F4EC] font-extrabold px-6 py-2.5 text-sm transition-colors">
                        <Receipt className="w-4 h-4" />
                        Registrar venda
                    </button>
                </div>
            </form>

            {msg && (
                <p data-testid="admin-vendas-message" className={`mt-4 rounded-xl px-4 py-3 text-sm font-semibold border-2 ${msg.ok ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}>
                    {msg.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> : null}
                    {msg.text}
                </p>
            )}

            <div className="mt-5 space-y-2.5" data-testid="vendas-list">
                {vendas.slice(0, 15).map((v) => (
                    <div key={v.id} data-testid="venda-item" className="flex items-center justify-between gap-4 rounded-2xl bg-white border-2 border-[#1A231E]/10 px-4.5 px-4 py-3">
                        <div className="min-w-0">
                            <p className="text-sm font-bold truncate">
                                {v.items.map((it) => `${it.qtd}× ${it.nome}`).join(", ")}
                                {v.cupom && <span className="ml-2 text-[10px] font-extrabold uppercase bg-[#F1C75B] px-2 py-0.5 rounded-full">{v.cupom} -{v.desconto_pct}%</span>}
                            </p>
                            <p className="text-xs font-bold text-[#1A231E]/40">
                                {new Date(v.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                            </p>
                        </div>
                        <span className="font-black text-[#0E6B3F] shrink-0">{fmtBRL(v.total)}</span>
                    </div>
                ))}
                {vendas.length === 0 && (
                    <p className="rounded-2xl border-2 border-dashed border-[#1A231E]/15 px-6 py-8 text-center text-sm font-semibold text-[#1A231E]/50">
                        Nenhuma venda registrada ainda.
                    </p>
                )}
            </div>
        </section>
    );
}
